/**
 * 앱 부팅·myprofile 이후 첫 화면까지 성능 디버그 로그.
 *
 * DevTools 콘솔 필터: app-perf
 * 프로덕션: window.__APP_PERF__ = true
 *
 * 수동 덤프: window.__dumpAppPerfTimeline()
 * 타임라인 배열: window.__APP_PERF_TIMELINE__
 */

import { getAxiosInstance } from '@/apis/base.api';
import { isAppDebugEnabled } from '@/utils/debug/debugEnv';

const NS = '[app-perf]';
export const APP_PERF_MYPROFILE_DONE = 'myprofile:done';

const marks = new Map();
/** @type {Array<{ label: string, t: number, sinceOrigin: number|null, sinceMyProfile: number|null, detail?: unknown }>} */
const timeline = [];

let httpInterceptorRegistered = false;
let bootstrapReportScheduled = false;

export function appPerfEnabled() {
  if (typeof window !== 'undefined') {
    if (window.__APP_PERF__ === true || window.__PANEL_PERF__ === true) {
      return true;
    }
  }
  return isAppDebugEnabled();
}

function nowMs() {
  return performance.now();
}

function sinceOriginMs(t = nowMs()) {
  const origin = marks.get('app:origin');
  return origin != null ? +(t - origin).toFixed(1) : null;
}

function sinceMyProfileMs(t = nowMs()) {
  const anchor = marks.get(APP_PERF_MYPROFILE_DONE);
  return anchor != null ? +(t - anchor).toFixed(1) : null;
}

function recordTimeline(label, detail) {
  const t = nowMs();
  const entry = {
    label,
    t: +t.toFixed(1),
    sinceOrigin: sinceOriginMs(t),
    sinceMyProfile: sinceMyProfileMs(t),
    ...(detail !== undefined ? { detail } : {})
  };
  timeline.push(entry);
  if (typeof window !== 'undefined') {
    window.__APP_PERF_TIMELINE__ = timeline;
  }
  return entry;
}

export function appPerfSetOrigin(label) {
  const t = nowMs();
  marks.set('app:origin', t);
  recordTimeline('app:origin', { label });
  if (!appPerfEnabled()) return;
  const nav = performance.timing?.navigationStart;
  const sinceNav = nav
    ? `${(Date.now() - nav).toFixed(0)}ms since navigationStart`
    : 'no navigationTiming';
  console.log(NS, 'origin', label, `@${t.toFixed(1)}ms`, sinceNav);
}

export function appPerfMark(label, detail) {
  marks.set(label, nowMs());
  recordTimeline(label, detail);
  if (!appPerfEnabled()) return;
  if (detail !== undefined) {
    console.log(NS, label, detail, `@${marks.get(label).toFixed(1)}ms`, {
      sinceOrigin: sinceOriginMs(),
      sinceMyProfile: sinceMyProfileMs()
    });
  } else {
    console.log(NS, label, `@${marks.get(label).toFixed(1)}ms`, {
      sinceOrigin: sinceOriginMs(),
      sinceMyProfile: sinceMyProfileMs()
    });
  }
}

export function appPerfMeasure(label, sinceLabel, detail) {
  const end = nowMs();
  const start = sinceLabel ? marks.get(sinceLabel) : undefined;
  const dur = start != null ? end - start : undefined;
  const durText = dur != null ? `${dur.toFixed(1)}ms` : '(no mark)';
  recordTimeline(label, {
    durMs: dur != null ? +dur.toFixed(1) : null,
    since: sinceLabel,
    ...(detail && typeof detail === 'object' ? detail : detail !== undefined ? { info: detail } : {})
  });
  if (!appPerfEnabled()) return dur;
  const payload = {
    dur: durText,
    sinceOrigin: sinceOriginMs(end),
    sinceMyProfile: sinceMyProfileMs(end),
    ...(detail !== undefined ? { detail } : {})
  };
  console.log(NS, label, payload);
  return dur;
}

/** myprofile 완료 시점부터의 경과 시간 */
export function appPerfSinceMyProfile(label, detail) {
  return appPerfMeasure(label, APP_PERF_MYPROFILE_DONE, detail);
}

export function appPerfMarkMyProfileDone(detail) {
  appPerfMark(APP_PERF_MYPROFILE_DONE, detail);
}

export async function appPerfAsync(label, fn) {
  if (!appPerfEnabled()) {
    return fn();
  }
  const start = nowMs();
  appPerfMark(`${label}:start`);
  try {
    const result = await fn();
    appPerfMeasure(`${label}:done`, `${label}:start`, {
      totalMs: +(nowMs() - start).toFixed(1)
    });
    return result;
  } catch (error) {
    appPerfMeasure(`${label}:error`, `${label}:start`, {
      totalMs: +(nowMs() - start).toFixed(1),
      message: error?.message || String(error)
    });
    throw error;
  }
}

export function appPerfSync(label, fn) {
  if (!appPerfEnabled()) {
    return fn();
  }
  const start = nowMs();
  appPerfMark(`${label}:start`);
  try {
    const result = fn();
    appPerfMeasure(`${label}:done`, `${label}:start`, {
      totalMs: +(nowMs() - start).toFixed(1)
    });
    return result;
  } catch (error) {
    appPerfMeasure(`${label}:error`, `${label}:start`, {
      totalMs: +(nowMs() - start).toFixed(1),
      message: error?.message || String(error)
    });
    throw error;
  }
}

/** dynamic import() 래퍼 — Dashboard lazy chunk 등 */
export function appPerfLazyChunk(chunkName, importFn) {
  return () => {
    appPerfMark(`chunk:${chunkName}:start`);
    return importFn().then((mod) => {
      appPerfMeasure(`chunk:${chunkName}:loaded`, `chunk:${chunkName}:start`);
      appPerfSinceMyProfile(`chunk:${chunkName}:ready`);
      return mod;
    });
  };
}

/** DOM 반영·첫 페인트 근사 (double rAF) */
export function appPerfScheduleFirstPaint(context, extra) {
  if (!appPerfEnabled()) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      appPerfSinceMyProfile(`firstPaint:${context}`, extra);
    });
  });
}

function shortApiPath(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = url.startsWith('http') ? new URL(url) : { pathname: url };
    return u.pathname || url;
  } catch {
    return url;
  }
}

/** 모든 REST 요청·응답 시간 (myprofile 이후 공백 vs API 구분용) */
export function registerAppPerfHttpInterceptor() {
  if (httpInterceptorRegistered || typeof window === 'undefined') {
    return;
  }
  httpInterceptorRegistered = true;

  const instance = getAxiosInstance();
  instance.interceptors.request.use((config) => {
    if (!appPerfEnabled()) return config;
    const path = shortApiPath(config.url);
    config.__appPerfStart = nowMs();
    recordTimeline('http:request', {
      method: (config.method || 'get').toUpperCase(),
      path,
      sinceMyProfile: sinceMyProfileMs(config.__appPerfStart)
    });
    console.log(NS, 'http:request', {
      method: (config.method || 'get').toUpperCase(),
      path,
      sinceOrigin: sinceOriginMs(config.__appPerfStart),
      sinceMyProfile: sinceMyProfileMs(config.__appPerfStart)
    });
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      if (appPerfEnabled() && response?.config?.__appPerfStart != null) {
        const start = response.config.__appPerfStart;
        const path = shortApiPath(response.config.url);
        const dur = nowMs() - start;
        const detail = {
          method: (response.config.method || 'get').toUpperCase(),
          path,
          status: response.status,
          durMs: +dur.toFixed(1)
        };
        recordTimeline('http:response', detail);
        console.log(NS, 'http:response', {
          ...detail,
          sinceOrigin: sinceOriginMs(),
          sinceMyProfile: sinceMyProfileMs()
        });
      }
      return response;
    },
    (error) => {
      if (appPerfEnabled() && error?.config?.__appPerfStart != null) {
        const start = error.config.__appPerfStart;
        const path = shortApiPath(error.config.url);
        const dur = nowMs() - start;
        const detail = {
          method: (error.config.method || 'get').toUpperCase(),
          path,
          status: error?.response?.status,
          durMs: +dur.toFixed(1)
        };
        recordTimeline('http:error', detail);
        console.log(NS, 'http:error', {
          ...detail,
          sinceOrigin: sinceOriginMs(),
          sinceMyProfile: sinceMyProfileMs()
        });
      }
      return Promise.reject(error);
    }
  );
}

function buildGapRows() {
  const rows = [];
  for (let i = 1; i < timeline.length; i += 1) {
    const prev = timeline[i - 1];
    const cur = timeline[i];
    rows.push({
      gapMs: +(cur.t - prev.t).toFixed(1),
      from: prev.label,
      to: cur.label,
      atSinceMyProfile: cur.sinceMyProfile
    });
  }
  return rows.sort((a, b) => b.gapMs - a.gapMs);
}

/** 콘솔에 전체 타임라인 + 큰 공백 구간 출력 */
export function appPerfDumpTimeline(reason = 'manual') {
  if (!appPerfEnabled() && typeof window !== 'undefined' && window.__APP_PERF__ !== true) {
    console.warn(NS, 'dump skipped — set window.__APP_PERF__ = true in production');
  }
  const gaps = buildGapRows();
  console.group(`${NS} timeline dump (${reason}) — ${timeline.length} events`);
  console.table(
    timeline.map((e) => ({
      label: e.label,
      t: e.t,
      sinceOrigin: e.sinceOrigin,
      sinceMyProfile: e.sinceMyProfile,
      detail: e.detail != null ? JSON.stringify(e.detail).slice(0, 120) : ''
    }))
  );
  console.log(`${NS} top gaps (myprofile 이후 공백 후보):`);
  console.table(gaps.slice(0, 15));
  const myprofileIdx = timeline.findIndex((e) => e.label === APP_PERF_MYPROFILE_DONE);
  if (myprofileIdx >= 0) {
    const nextHttp = timeline.findIndex(
      (e, i) => i > myprofileIdx && e.label === 'http:request'
    );
    if (nextHttp >= 0) {
      const gap = timeline[nextHttp].t - timeline[myprofileIdx].t;
      console.log(`${NS} myprofile → 첫 http:request 공백: ${gap.toFixed(1)}ms`, {
        firstApi: timeline[nextHttp].detail
      });
    } else {
      console.log(`${NS} myprofile 이후 http:request 없음 (아직 진행 중이거나 API 미호출)`);
    }
  }
  console.groupEnd();
}

/** 부팅 후 자동 리포트 (기본 20초) */
export function appPerfScheduleBootstrapReport(delayMs = 20000) {
  if (bootstrapReportScheduled || typeof window === 'undefined') {
    return;
  }
  bootstrapReportScheduled = true;
  window.__dumpAppPerfTimeline = () => appPerfDumpTimeline('manual');
  setTimeout(() => {
    appPerfDumpTimeline(`auto-${delayMs}ms`);
  }, delayMs);
}
